import { MentionType, TemplateDirectionEnum, TemplateLocaleEnum } from '__generated__/types';
import { FormInstance, Modal } from 'antd';
import {
  StateAttachment,
  TemplateModalFormFieldEnum,
  TemplateModalFormValues,
} from 'components/TemplatesModal/TemplateModal.types';
import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';

import { LocalesMap } from './useTemplatesModalFormDerived';

interface Args {
  form: FormInstance<TemplateModalFormValues>;
  changesMadeRef?: MutableRefObject<boolean>;
  clientOrPartnerLocale: string;
  templateMapByConstantName: Record<string, LocalesMap>;
  setSelectedCategoryId: Dispatch<SetStateAction<string | null>>;
  setSelectedTemplate: Dispatch<SetStateAction<TemplateFragment | null>>;
  setAttachments: Dispatch<SetStateAction<StateAttachment[]>>;
  setMessageVisibleTo: (value: 'client' | 'partner' | null) => void;
}

const getVisibleToByDirection = (direction?: TemplateDirectionEnum) => {
  if (direction === TemplateDirectionEnum.X2c) {
    return 'client';
  }

  if (direction === TemplateDirectionEnum.X2p) {
    return 'partner';
  }

  return null;
};

export const useTemplatesModalFormHandlers = ({
  form,
  changesMadeRef,
  clientOrPartnerLocale,
  templateMapByConstantName,
  setSelectedCategoryId,
  setSelectedTemplate,
  setAttachments,
  setMessageVisibleTo,
}: Args) => {
  const runWithDiscardChangesConfirm = useCallback(
    (title: string, onOk: () => void) => {
      if (!changesMadeRef?.current) {
        onOk();

        return;
      }

      Modal.confirm({
        title,
        content: 'Changes you made may not be saved',
        onOk() {
          changesMadeRef.current = false;
          onOk();
        },
        onCancel() {
          return;
        },
      });
    },
    [changesMadeRef],
  );

  const handleTemplateSelect = useCallback(
    (template: TemplateFragment | undefined) => {
      form.setFieldValue(TemplateModalFormFieldEnum.To, []);
      form.setFieldValue(TemplateModalFormFieldEnum.Cc, []);
      form.setFieldValue(TemplateModalFormFieldEnum.Bcc, []);
      form.setFieldValue(TemplateModalFormFieldEnum.Content, '');

      setAttachments([]);
      setSelectedTemplate(template || null);
    },
    [form, setAttachments, setSelectedTemplate],
  );

  const handleCategoryChange = useCallback(
    (categoryId: string) => {
      runWithDiscardChangesConfirm('Change template category?', () => {
        setSelectedCategoryId(categoryId);
      });
    },
    [runWithDiscardChangesConfirm, setSelectedCategoryId],
  );

  const handleTemplateConstantNameChange = useCallback(
    (newTemplateConstantName: string) => {
      form.setFieldValue(TemplateModalFormFieldEnum.TemplateConstantName, newTemplateConstantName);

      const localesOfTemplate = templateMapByConstantName[newTemplateConstantName];

      if (!localesOfTemplate) {
        handleTemplateSelect(undefined);

        return;
      }

      const hasClientOrPartnerLocale = Object.keys(localesOfTemplate).includes(clientOrPartnerLocale);
      const formLocale = form.getFieldValue(TemplateModalFormFieldEnum.Locale) as TemplateLocaleEnum;
      const resolvedLocale = hasClientOrPartnerLocale
        ? clientOrPartnerLocale
        : localesOfTemplate[formLocale]
          ? formLocale
          : (Object.keys(localesOfTemplate)[0] as TemplateLocaleEnum);

      form.setFieldValue(TemplateModalFormFieldEnum.Locale, resolvedLocale);

      const newTemplate = localesOfTemplate[resolvedLocale as TemplateLocaleEnum];

      if (newTemplate?.direction !== TemplateDirectionEnum.Any) {
        setMessageVisibleTo(getVisibleToByDirection(newTemplate?.direction));
      }

      runWithDiscardChangesConfirm('Change template name?', () => {
        handleTemplateSelect(newTemplate);
      });
    },
    [
      clientOrPartnerLocale,
      form,
      handleTemplateSelect,
      runWithDiscardChangesConfirm,
      setMessageVisibleTo,
      templateMapByConstantName,
    ],
  );

  const handleLocaleChange = useCallback(
    (locale: string) => {
      const templateConstantName = form.getFieldValue(TemplateModalFormFieldEnum.TemplateConstantName) as string;
      const newTemplate = templateMapByConstantName[templateConstantName]?.[locale as TemplateLocaleEnum];

      runWithDiscardChangesConfirm('Change locale?', () => {
        handleTemplateSelect(newTemplate);
      });
    },
    [form, handleTemplateSelect, runWithDiscardChangesConfirm, templateMapByConstantName],
  );

  const handleMentionSelect = useCallback(
    (user: MentionType) => {
      const currentMentions = form.getFieldValue(TemplateModalFormFieldEnum.Mentions) as MentionType[] | undefined;

      form.setFieldValue(TemplateModalFormFieldEnum.Mentions, [...(currentMentions || []), user]);
    },
    [form],
  );

  const handleMentionDeselect = useCallback(
    (userId: string) => {
      const currentMentions = form.getFieldValue(TemplateModalFormFieldEnum.Mentions) as MentionType[];

      form.setFieldValue(
        TemplateModalFormFieldEnum.Mentions,
        currentMentions.filter(({ id }) => id !== String(userId)),
      );
    },
    [form],
  );

  const handleToChange = useCallback(
    (value: string[]) => {
      form.setFieldValue(TemplateModalFormFieldEnum.To, value);
    },
    [form],
  );

  const handleCcChange = useCallback(
    (value: string[]) => {
      form.setFieldValue(TemplateModalFormFieldEnum.Cc, value);
    },
    [form],
  );

  const handleBccChange = useCallback(
    (value: string[]) => {
      form.setFieldValue(TemplateModalFormFieldEnum.Bcc, value);
    },
    [form],
  );

  return {
    handleTemplateSelect,
    handleCategoryChange,
    handleTemplateConstantNameChange,
    handleLocaleChange,
    handleMentionSelect,
    handleMentionDeselect,
    handleToChange,
    handleCcChange,
    handleBccChange,
  };
};
